import { SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { TweenConstants } from "@lib/titan2-slingo/dist/game/view/abstract/constant/TweenConstants";
import { BigWin } from "@lib/titan2-slingo/dist/game/view/abstract/display/BigWin";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { DestroyUtil } from "@lib/titan2/dist/util/view/DestroyUtil";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { TitanCurrencyTextSelector } from "../../../utils/FancyText/TitanCurrencyTextSelector";
import { TitanFancyTextFactory } from "../../../utils/FancyText/TitanFancyTextFactory";
import { GameSpineUtil } from "../../../utils/GameSpineUtil";
import { GameTextUtil } from "../../../utils/GameTextUtil";
/**
 * Shows a big win animation with big win -> epic win -> mega win levels.
 */
export class GameBigWin extends BigWin {
    create() {
        super.create();
        this.fireworkBurst = this.factory.createFireworkBurst();
        this.title0 = TitanFancyTextFactory.CreateElement(this, this.langManager, this.landscapeProperties.title0Properties, this.portraitProperties?.title0Properties);
        this.title1 = TitanFancyTextFactory.CreateElement(this, this.langManager, this.landscapeProperties.title1Properties, this.portraitProperties?.title1Properties);
        this.title2 = TitanFancyTextFactory.CreateElement(this, this.langManager, this.landscapeProperties.title2Properties, this.portraitProperties?.title2Properties);
        this.title3 = TitanFancyTextFactory.CreateElement(this, this.langManager, this.landscapeProperties.title3Properties, this.portraitProperties?.title3Properties);
        [this.title0, this.title1, this.title2, this.title3].forEach(x => {
            if (x instanceof TitanText) {
                GameTextUtil.forceInitialise(x);
            }
        });
        GameTextUtil.forceInitialise(this.counter);
        DestroyUtil.destroy(this.counter);
        this.counter = TitanCurrencyTextSelector.createField(this.factory, this, this.landscapeProperties.bitmapCounter, this.portraitProperties?.bitmapCounter);
        GameSpineUtil.addToSlot(this.animation, this.counter, "dynamic-value");
    }
    createMouseBlocker() {
        super.createMouseBlocker();
        this.mouseBlocker.onClick(() => this.timelineAnimation?.seek(this.timelineAnimation.duration(), false), this);
    }
    reset() {
        super.reset();
        this.timelineAnimation?.kill();
        this.timelineAnimation = undefined;
        this.mouseBlocker.interactiveChildren = false;
        this.visible = false;
    }
    handleRotate(event) {
        super.handleRotate(event);
        GameSpineUtil.addToSlot(this.animation, this.fireworkBurst, "dynamic-fireworks");
        GameSpineUtil.addToSlot(this.animation, this.counter, "dynamic-value");
        GameSpineUtil.addToSlot(this.animation, this.title0, "dynamic-label-0");
        GameSpineUtil.addToSlot(this.animation, this.title1, "dynamic-label-1");
        GameSpineUtil.addToSlot(this.animation, this.title2, "dynamic-label-2");
        GameSpineUtil.addToSlot(this.animation, this.title3, "dynamic-label-3");
    }
    async handlePlayBigWin() {
        this.visible = true;
        await Promise.all([this.playAppear(), this.playCount()]);
        await this.playDisappear();
        this.handleAnimationComplete();
    }
    async playAppear() {
        this.dispatchGame(SlingoEvent.BIG_WIN_APPEAR);
        this.fireworkBurst.start();
        this.showOverlay();
        SpineUtil.playLoop(this.animation, "loop", 1);
        await gsap.fromTo(this.animation, { alpha: 0 }, { alpha: 1, duration: TweenConstants.DEFAULT_OVERLAY_FADE_DURATION });
    }
    async playDisappear() {
        this.dispatchGame(SlingoEvent.BIG_WIN_DISAPPEAR);
        this.fireworkBurst.stop();
        this.hideOverlay();
        const multiplier = this.getMultiplier();
        const bigWinLevel = this.model.getBigWinLevel(multiplier);
        if (bigWinLevel >= 0) {
            this.hideBigWin(bigWinLevel);
        }
        else {
            this.hideWin();
        }
        await gsap.to(this.animation, { alpha: 0, duration: TweenConstants.DEFAULT_OVERLAY_FADE_DURATION });
    }
    async playCount() {
        this.dispatchGame(SlingoEvent.BIG_WIN_COUNT);
        this.timelineAnimation?.kill();
        const timeline = gsap.timeline();
        this.timelineAnimation = timeline;
        const totalWin = this.model.getTotalWin();
        const totalDuration = this.getCounterDuration();
        const thresholdValues = this.getThresholdValues(totalWin, this.model.getTotalStaked());
        const counterDuration = totalDuration / (thresholdValues.length + 1);
        this.initialiseCounter();
        this.showWin();
        // Count up to each threshold
        thresholdValues.forEach((threshold, index) => {
            timeline
                .to(this, {
                counterValue: threshold,
                duration: counterDuration,
                onUpdate: () => this.updateCounter(),
                ease: "power1.in",
            })
                .call(() => this.showBigWin(index));
        });
        // Prevent rollup to total win from being slower than the last threshold
        const lastThresholdValue = thresholdValues[thresholdValues.length - 1];
        const secondLastThresholdValue = thresholdValues[thresholdValues.length - 2] || 0;
        const lastThresholdRateOfIncrease = (lastThresholdValue - secondLastThresholdValue) / counterDuration;
        const totalWinRateOfIncrease = (totalWin - lastThresholdValue) / counterDuration;
        const remainingDuration = lastThresholdRateOfIncrease > 0 ? totalWinRateOfIncrease / lastThresholdRateOfIncrease : counterDuration;
        // console.log("BIG WIN", thresholdValues, counterDuration, remainingDuration)
        // Count up to the total win amount
        timeline.to(this, {
            counterValue: totalWin,
            duration: Math.min(counterDuration, remainingDuration),
            onUpdate: () => this.updateCounter(),
            ease: "power1.in",
        });
        this.mouseBlocker.interactiveChildren = true;
        await this.timelineAnimation;
        this.mouseBlocker.interactiveChildren = false;
        this.dispatchGame(SlingoEvent.BIG_WIN_COUNT_END);
        SpineUtil.play(this.animation, "celebrate", null, null, 2);
        this.timelineAnimation = gsap.to(this, { duration: 3 });
        await this.timelineAnimation;
    }
    initialiseCounter() {
        this.counter.setText(this.formatCurrency(0));
    }
    updateCounter() {
        this.counter.setText(this.formatCurrency(this.counterValue));
    }
    showWin() {
        SpineUtil.play(this.animation, "0-win");
    }
    hideWin() {
        SpineUtil.play(this.animation, "0-win-out");
    }
    showBigWin(level) {
        SpineUtil.play(this.animation, `${level + 1}-win`);
    }
    hideBigWin(level) {
        SpineUtil.play(this.animation, `${level + 1}-win-out`);
    }
    getCounterDuration() {
        const multiplier = this.getMultiplier();
        const bigWinLevel = this.model.getBigWinLevel(multiplier);
        return this.uiProperties.counterTimes[bigWinLevel + 1];
    }
    getMultiplier() {
        return this.model.getTotalWin() / this.model.getStake().getCurrent();
    }
    getThresholdValues(win, totalStake) {
        return this.model.getBigWinThresholds()
            .map(threshold => threshold * totalStake)
            .filter(threshold => win >= threshold);
    }
}
//# sourceMappingURL=GameBigWin.js.map